import { useMemo, useState } from 'react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/ui/base/collapsible';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/base/popover';
import { Progress } from '@/ui/base/progress';
import { MultiSelectCombobox } from '@/ui/base/multi-select-combobox';
import { Skeleton } from '@/ui/base/skeleton';
import { Textarea } from '@/ui/base/textarea';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { exportarCsv } from '@/ui/exportCsv';
import { FilterBar } from '@/ui/FilterBar';
import { MonthFilter } from '@/ui/MonthFilter';
import { PageHeader } from '@/ui/PageHeader';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { STATUS_TONE, type StatusTone } from '@/nucleo/statusBadgeTokens';
import { cn } from '@/nucleo/utilitarios';
import {
  Ban,
  Building2,
  Bus,
  CalendarDays,
  CalendarRange,
  Car,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  Eye,
  IdCard,
  Keyboard,
  Layers,
  LayoutGrid,
  List,
  Lock,
  Maximize2,
  Minimize2,
  Send,
  ThumbsDown,
  ThumbsUp,
  User,
  Users,
} from 'lucide-react';

import {
  useAplicarLoteCelula,
  useApontamentos,
  useSalvarApontamentoCelula,
} from '../hooks/useApontamentos';
import { useDres } from '../hooks/useDres';
import { NaturezaToggle } from '../fechamento-v2/NaturezaToggle';
import type {
  ApontamentoDiaLinha,
  CelulaApontamentoDia,
  ModalidadeAtendimento,
  StatusApontamentoOs,
  SubLinhaApontamento,
} from '../types';

import { SUBLINHA_LABEL } from './sublinhas';

function competenciaAtual(): string {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
}

const MODALIDADE_LABEL: Record<ModalidadeAtendimento, string> = {
  CONVENCIONAL: 'Convencional',
  ACESSIVEL: 'Acessível',
  CRECHE: 'Creche',
};

const STATUS_OS_INFO: Record<StatusApontamentoOs, { label: string; tone: StatusTone }> = {
  RASCUNHO_DRE: { label: 'Rascunho DRE', tone: 'neutral' },
  ENVIADO_SME: { label: 'Enviado à SME', tone: 'info' },
  APROVADO_SME: { label: 'Aprovado pela SME', tone: 'success' },
  RECEBIDO_DTP: { label: 'Recebido pelo DTP', tone: 'success' },
  DEVOLVIDO: { label: 'Devolvido', tone: 'destructive' },
};

const STATUS_OS_OPCOES: { value: StatusApontamentoOs | 'TODOS'; label: string }[] = [
  { value: 'TODOS', label: 'Todos os status' },
  { value: 'RASCUNHO_DRE', label: 'Rascunho DRE' },
  { value: 'ENVIADO_SME', label: 'Enviado para SME' },
  { value: 'APROVADO_SME', label: 'Aprovado pela SME' },
  { value: 'RECEBIDO_DTP', label: 'Recebido pelo DTP' },
  { value: 'DEVOLVIDO', label: 'Devolvido para correção' },
];

const SUBLINHAS: SubLinhaApontamento[] = [
  'NCAD',
  'CAD',
  'NC_CONT',
  'CAD_CONT',
  'NC_COMP',
  'CAD_COMP',
  'FALTAS',
  'FALTA_VEICULO',
];

const SUBLINHA_CAMPO: Partial<Record<SubLinhaApontamento, keyof CelulaApontamentoDia>> = {
  NCAD: 'naoCadeirante',
  CAD: 'cadeirante',
  NC_CONT: 'continuadaNaoCadeirante',
  CAD_CONT: 'continuadaCadeirante',
  NC_COMP: 'complementarNaoCadeirante',
  CAD_COMP: 'complementarCadeirante',
  FALTAS: 'faltasAlunos',
};

/** Valor numérico de uma sublinha na célula do dia — `FALTA_VEICULO` é
 * booleana e não passa por aqui (ver `celulaVazia`). */
function valorSublinha(cel: CelulaApontamentoDia | undefined, sub: SubLinhaApontamento): number {
  if (!cel) return 0;
  const campo = SUBLINHA_CAMPO[sub];
  return campo ? (cel[campo] as number) : 0;
}

/** Um dia conta como "sem lançamento" quando nenhuma sublinha numérica tem
 * valor e o veículo não foi marcado como ausente. */
function celulaVazia(cel: CelulaApontamentoDia | undefined): boolean {
  if (!cel) return true;
  return (
    cel.naoCadeirante === 0 &&
    cel.cadeirante === 0 &&
    cel.continuadaNaoCadeirante === 0 &&
    cel.continuadaCadeirante === 0 &&
    cel.complementarNaoCadeirante === 0 &&
    cel.complementarCadeirante === 0 &&
    cel.faltasAlunos === 0 &&
    !cel.faltaVeiculo
  );
}

/** Quantos dias letivos da OS já têm algum lançamento — base da barra de
 * progresso do card da DRE e do chip "dias sem lançto" da OS. */
function diasPreenchidos(linha: ApontamentoDiaLinha): number {
  return linha.diasLetivos.filter((d) => !celulaVazia(linha.celulas[d])).length;
}

/** Dia do mês em que a vigência da OS termina, se houver (o resto do mês
 * fica "fora da vigência" na grade). */
function vigenciaFimDia(linha: ApontamentoDiaLinha): number | null {
  if (!linha.vigenciaFim) return null;
  return Number(linha.vigenciaFim.slice(8, 10));
}

type StatusCardDre = 'nao_iniciado' | 'atencao' | 'aprovado' | 'em_validacao' | 'em_andamento';

const STATUS_CARD_DRE_INFO: Record<StatusCardDre, { label: string; tone: StatusTone }> = {
  nao_iniciado: { label: 'Não iniciado', tone: 'neutral' },
  atencao: { label: 'Atenção', tone: 'warning' },
  aprovado: { label: 'Aprovado', tone: 'success' },
  em_validacao: { label: 'Em validação', tone: 'info' },
  em_andamento: { label: 'Em andamento', tone: 'neutral' },
};

function resolverStatusCardDre(linhas: ApontamentoDiaLinha[]): StatusCardDre {
  if (linhas.length === 0) return 'nao_iniciado';
  const devolvidas = linhas.filter((l) => l.status === 'DEVOLVIDO').length;
  if (devolvidas > 0) return 'atencao';
  const aprovadas = linhas.filter(
    (l) => l.status === 'APROVADO_SME' || l.status === 'RECEBIDO_DTP',
  ).length;
  if (aprovadas === linhas.length) return 'aprovado';
  const rascunhos = linhas.filter((l) => l.status === 'RASCUNHO_DRE').length;
  if (rascunhos === linhas.length) return 'nao_iniciado';
  const enviadas = linhas.filter((l) => l.status === 'ENVIADO_SME').length;
  if (enviadas > 0) return 'em_validacao';
  return 'em_andamento';
}

function TomBadge({
  tone,
  label,
  className,
}: {
  tone: StatusTone;
  label: string;
  className?: string;
}) {
  return (
    <Badge variant="outline" className={cn('font-medium', STATUS_TONE[tone], className)}>
      {label}
    </Badge>
  );
}

interface ApontamentoGridPageProps {
  /** Quando embutida (Fechamento Mensal V2), mês e natureza já vêm da URL do
   * fechamento — o cabeçalho de página e o toggle de natureza ficam ocultos. */
  embedded?: boolean;
  mesReferencia?: string;
  natureza?: 'PF' | 'PJ';
}

/**
 * Grade de apontamentos diários por OS — standalone
 * (`/operacao/financeiro/apontamentos`) e embutida na aba "Apontamentos" do
 * Fechamento Mensal V2. Agrupa por DRE (um card por DRE, com o lote SME
 * daquela DRE) ou mostra a lista de OS sem agrupar.
 *
 * Fora do escopo desta tela (dependem de motor de cálculo ou integração real,
 * que ainda não existem — CLAUDE.md, "Regras de interface", item 7):
 * - alternância "Por dia" (grade dia a dia): no protótipo já não estava
 *   ligada a nenhum cálculo real, então só "Por mês" foi implementado aqui;
 * - "Anexar" e "Histórico" por DRE: sem armazenamento de arquivo nem trilha
 *   de auditoria reais, esses botões não são renderizados. "Exportar" ficou,
 *   porque é puramente local (CSV gerado no navegador).
 */
export function ApontamentoGridPage({
  embedded = false,
  mesReferencia,
  natureza: naturezaProp,
}: ApontamentoGridPageProps) {
  const [mes, definirMes] = useState(mesReferencia ?? competenciaAtual());
  const [naturezaLocal, definirNaturezaLocal] = useState<'PF' | 'PJ'>('PF');
  const natureza = embedded ? (naturezaProp ?? 'PF') : naturezaLocal;

  const [dreIds, definirDreIds] = useState<string[]>([]);
  const [busca, definirBusca] = useState('');
  const [statusFiltro, definirStatusFiltro] = useState<StatusApontamentoOs | 'TODOS'>('TODOS');
  const [agrupamento, definirAgrupamento] = useState<'DRE' | 'OS'>('DRE');
  const [modoVisualizacao, definirModoVisualizacao] = useState<'mes' | 'dia'>('mes');
  const [diaSelecionado, definirDiaSelecionado] = useState(1);
  const [edicoesKm, definirEdicoesKm] = useState<Record<string, string>>({});
  const [edicoesCelulas, definirEdicoesCelulas] = useState<
    Record<string, Record<number, Partial<CelulaApontamentoDia>>>
  >({});
  const [dresAbertas, definirDresAbertas] = useState<Set<string>>(new Set());
  const [osAbertas, definirOsAbertas] = useState<Set<string>>(new Set());
  const [conferindo, definirConferindo] = useState<{ dreId: string; dreNome: string } | null>(null);
  const [devolvendo, definirDevolvendo] = useState<{ dreId: string; dreNome: string } | null>(null);
  const [motivoDevolucao, definirMotivoDevolucao] = useState('');

  const { data: dres } = useDres();
  const { data, isLoading, isError, refetch } = useApontamentos({
    mes,
    dreIds,
    busca: busca || undefined,
    natureza,
  });
  const salvarCelula = useSalvarApontamentoCelula();
  const aplicarLote = useAplicarLoteCelula();
  // Aprovar, devolver e enviar lote ainda não têm endpoint: mostram o aviso de
  // indisponibilidade e não mudam nada (CLAUDE.md, exceção do Fechamento
  // Mensal, item 2). Não existe gancho de escrita para isso, de propósito.
  const avisarIndisponivel = () => mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);

  const linhasBase = useMemo(() => {
    const base = data ?? [];
    return base.map((linha) => {
      const kmEditado = edicoesKm[linha.ordemServicoId];
      const celulasEditadas = edicoesCelulas[linha.ordemServicoId];
      if (!kmEditado && !celulasEditadas) return linha;
      return {
        ...linha,
        km: kmEditado ?? linha.km,
        celulas: celulasEditadas
          ? Object.fromEntries(
              linha.diasLetivos.map((d) => [
                d,
                { ...linha.celulas[d], ...celulasEditadas[d] } as CelulaApontamentoDia,
              ]),
            )
          : linha.celulas,
      };
    });
  }, [data, edicoesKm, edicoesCelulas]);

  const linhasExibidas = useMemo(
    () =>
      statusFiltro === 'TODOS' ? linhasBase : linhasBase.filter((l) => l.status === statusFiltro),
    [linhasBase, statusFiltro],
  );

  const gruposDre = useMemo(() => {
    const mapa = new Map<string, ApontamentoDiaLinha[]>();
    for (const linha of linhasBase) {
      const lista = mapa.get(linha.dreId) ?? [];
      lista.push(linha);
      mapa.set(linha.dreId, lista);
    }
    return new Map(
      [...mapa.entries()].sort((a, b) =>
        (a[1][0]?.dreCodigo ?? '').localeCompare(b[1][0]?.dreCodigo ?? ''),
      ),
    );
  }, [linhasBase]);

  const exibidasPorDre = useMemo(() => {
    const mapa = new Map<string, ApontamentoDiaLinha[]>();
    for (const linha of linhasExibidas) {
      const lista = mapa.get(linha.dreId) ?? [];
      lista.push(linha);
      mapa.set(linha.dreId, lista);
    }
    return mapa;
  }, [linhasExibidas]);

  function editarKm(ordemServicoId: string, valor: string) {
    definirEdicoesKm((atual) => ({ ...atual, [ordemServicoId]: valor }));
  }

  function editarCelula(
    ordemServicoId: string,
    dia: number,
    sub: SubLinhaApontamento,
    valor: number | boolean,
  ) {
    const campo = sub === 'FALTA_VEICULO' ? 'faltaVeiculo' : SUBLINHA_CAMPO[sub]!;
    definirEdicoesCelulas((atual) => ({
      ...atual,
      [ordemServicoId]: {
        ...atual[ordemServicoId],
        [dia]: { ...atual[ordemServicoId]?.[dia], [campo]: valor },
      },
    }));
    salvarCelula.mutate({ ordemServicoId, dia, sub, valor });
  }

  function aplicarEmLote(
    ordemServicoId: string,
    dias: number[],
    sub: SubLinhaApontamento,
    valor: number,
  ) {
    const campo = SUBLINHA_CAMPO[sub]!;
    definirEdicoesCelulas((atual) => {
      const doIs = { ...atual[ordemServicoId] };
      for (const d of dias) doIs[d] = { ...doIs[d], [campo]: valor };
      return { ...atual, [ordemServicoId]: doIs };
    });
    aplicarLote.mutate({ ordemServicoId, dias, sub, valor });
  }

  function alternarOs(id: string) {
    definirOsAbertas((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });
  }

  function alternarDre(id: string) {
    definirDresAbertas((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });
  }

  const todasDresAbertas = gruposDre.size > 0 && dresAbertas.size === gruposDre.size;
  function alternarTodasDres() {
    definirDresAbertas(todasDresAbertas ? new Set() : new Set(gruposDre.keys()));
  }

  const todosOsIds = linhasExibidas.map((l) => l.ordemServicoId);
  const todasOsAbertas = todosOsIds.length > 0 && osAbertas.size === todosOsIds.length;
  function alternarTodasOs() {
    definirOsAbertas(todasOsAbertas ? new Set() : new Set(todosOsIds));
  }

  const totalDresFiltradas = gruposDre.size;
  const dresAbertasVisiveis = [...dresAbertas].filter((id) => gruposDre.has(id)).length;

  function exportarDreCsv(dreCodigo: string | null, linhas: ApontamentoDiaLinha[]) {
    exportarCsv(
      `apontamentos-${dreCodigo ?? 'dre'}-${natureza}-${mes}.csv`,
      [
        'OS',
        'Credenciado',
        'Natureza',
        'Modalidade',
        'Status',
        'KM',
        'Dias preenchidos',
        'Dias úteis',
      ],
      linhas.map((l) => [
        String(l.numeroOs),
        l.credenciadoNome,
        l.natureza,
        MODALIDADE_LABEL[l.modalidade],
        STATUS_OS_INFO[l.status].label,
        l.km,
        String(diasPreenchidos(l)),
        String(l.diasLetivos.length),
      ]),
    );
  }

  return (
    <div className="space-y-6">
      {!embedded && (
        <PageHeader
          eyebrow="Financeiro · Grid"
          title="Apontamentos"
          description="Lançamento mensal de apontamentos financeiros por OS, agrupado por DRE."
        >
          <NaturezaToggle value={naturezaLocal} onChange={definirNaturezaLocal} />
          <MonthFilter value={mes} onChange={definirMes} hideLabel />
        </PageHeader>
      )}

      <FilterBar
        fields={[
          {
            key: 'busca',
            label: 'Buscar',
            type: 'search',
            placeholder: 'Nome do credenciado, nº OS, condutor ou placa',
            ariaLabel: 'Buscar por credenciado, OS, condutor ou placa',
          },
          {
            key: 'status',
            label: 'Status',
            type: 'select',
            width: 'w-full sm:w-[200px]',
            showAllOption: false,
            options: STATUS_OS_OPCOES.map((o) => ({ value: o.value, label: o.label })),
          },
        ]}
        values={{ busca, status: statusFiltro }}
        onChange={(chave, valor) => {
          if (chave === 'busca') definirBusca(valor);
          else definirStatusFiltro(valor as StatusApontamentoOs | 'TODOS');
        }}
        onClear={() => {
          definirBusca('');
          definirDreIds([]);
          definirStatusFiltro('TODOS');
        }}
        defaultValues={{ busca: '', status: 'TODOS' }}
        hasActiveFilters={busca.length > 0 || dreIds.length > 0 || statusFiltro !== 'TODOS'}
      >
        <div className="space-y-1">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">DRE</Label>
          <MultiSelectCombobox
            options={(dres ?? []).map((d) => ({
              value: d.id,
              label: d.nome ?? d.id,
              code: d.codigo ?? undefined,
            }))}
            selected={dreIds}
            onChange={definirDreIds}
            placeholder="Todas as DREs"
            searchPlaceholder="Buscar DRE..."
            emptyMessage="Nenhuma DRE encontrada."
            renderSelectedText={(n) => `${n} DRE${n > 1 ? 's' : ''} selecionada${n > 1 ? 's' : ''}`}
            triggerClassName="w-full sm:w-[220px]"
            ariaLabel="Filtrar por DRE"
          />
        </div>
      </FilterBar>

      {isError ? (
        <ErrorState
          variant="load-failed"
          primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
        />
      ) : isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      ) : linhasBase.length === 0 ? (
        <EmptyState
          title="Nenhuma OS ativa no mês"
          description="Ajuste a competência, a DRE, a natureza ou a busca."
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border bg-muted/30 px-3 py-2 text-sm">
            <span className="text-xs font-medium text-muted-foreground">Visualizar:</span>
            <div
              role="group"
              aria-label="Por mês ou por dia"
              className="mr-1 inline-flex items-center rounded-md border border-border bg-background p-0.5"
            >
              <button
                type="button"
                onClick={() => definirModoVisualizacao('mes')}
                aria-pressed={modoVisualizacao === 'mes'}
                aria-label="Grade do mês inteiro"
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-sm px-2 py-1 text-xs font-medium transition-colors',
                  'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
                  modoVisualizacao === 'mes'
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <CalendarRange className="h-3.5 w-3.5" aria-hidden="true" />
                Por mês
              </button>
              <button
                type="button"
                onClick={() => definirModoVisualizacao('dia')}
                aria-pressed={modoVisualizacao === 'dia'}
                aria-label="Lançamento de um único dia"
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-sm px-2 py-1 text-xs font-medium transition-colors',
                  'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
                  modoVisualizacao === 'dia'
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                Por dia
              </button>
            </div>

            {modoVisualizacao === 'dia' && (
              <div className="mr-1 inline-flex items-center gap-1 rounded-md border border-border bg-background p-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={diaSelecionado <= 1}
                  onClick={() => definirDiaSelecionado((d) => Math.max(1, d - 1))}
                  aria-label="Dia anterior"
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                </Button>
                <span className="min-w-[70px] text-center text-xs font-medium tabular-nums">
                  {new Date(
                    `${mes}-${String(diaSelecionado).padStart(2, '0')}T00:00:00`,
                  ).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() =>
                    definirDiaSelecionado((d) =>
                      Math.min(
                        new Date(Number(mes.slice(0, 4)), Number(mes.slice(5, 7)), 0).getDate(),
                        d + 1,
                      ),
                    )
                  }
                  aria-label="Próximo dia"
                >
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            )}

            {modoVisualizacao === 'mes' && (
              <>
                <div
                  role="group"
                  aria-label="Agrupamento da grade"
                  className="mr-1 inline-flex items-center rounded-md border border-border bg-background p-0.5"
                >
                  <button
                    type="button"
                    onClick={() => definirAgrupamento('DRE')}
                    aria-pressed={agrupamento === 'DRE'}
                    aria-label="Visualizar agrupado por DRE"
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-sm px-2 py-1 text-xs font-medium transition-colors',
                      'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
                      agrupamento === 'DRE'
                        ? 'bg-accent text-accent-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <LayoutGrid className="h-3.5 w-3.5" aria-hidden="true" />
                    Por DRE
                  </button>
                  <button
                    type="button"
                    onClick={() => definirAgrupamento('OS')}
                    aria-pressed={agrupamento === 'OS'}
                    aria-label="Visualizar lista plana por OS"
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-sm px-2 py-1 text-xs font-medium transition-colors',
                      'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
                      agrupamento === 'OS'
                        ? 'bg-accent text-accent-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <List className="h-3.5 w-3.5" aria-hidden="true" />
                    Por OS
                  </button>
                </div>

                <span className="font-medium text-foreground">
                  {linhasExibidas.length} {linhasExibidas.length === 1 ? 'OS' : 'OSs'}
                  {agrupamento === 'DRE' && ` · ${dresAbertasVisiveis}/${totalDresFiltradas} DREs`}
                </span>
              </>
            )}

            <div className="ml-auto flex flex-wrap items-center gap-2">
              {mes !== competenciaAtual() && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-warning/50 bg-warning/10 text-warning hover:bg-warning/20"
                  onClick={() => definirMes(competenciaAtual())}
                >
                  <Lock className="mr-2 h-4 w-4" aria-hidden="true" />
                  Somente leitura · voltar p/ {competenciaAtual()}
                </Button>
              )}
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" size="sm">
                    <Keyboard className="mr-2 h-4 w-4" aria-hidden="true" />
                    Atalhos
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="end">
                  <p className="mb-2 text-sm font-medium">Atalhos de teclado</p>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>Tab / Enter — próxima célula</li>
                    <li>Setas — navegar entre células</li>
                    <li>Ctrl + → — replicar valor na linha</li>
                    <li>Ctrl + D — copiar do dia letivo anterior</li>
                  </ul>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Lista informativa — a navegação por teclado ainda não está implementada nesta
                    grade.
                  </p>
                </PopoverContent>
              </Popover>

              {modoVisualizacao === 'mes' && agrupamento === 'DRE' && (
                <Button type="button" variant="outline" size="sm" onClick={alternarTodasDres}>
                  {todasDresAbertas ? (
                    <Minimize2 className="mr-2 h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Maximize2 className="mr-2 h-4 w-4" aria-hidden="true" />
                  )}
                  {todasDresAbertas ? 'Recolher DREs' : 'Expandir DREs'}
                </Button>
              )}
              {modoVisualizacao === 'mes' && (
                <Button type="button" variant="outline" size="sm" onClick={alternarTodasOs}>
                  {todasOsAbertas ? (
                    <Minimize2 className="mr-2 h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Maximize2 className="mr-2 h-4 w-4" aria-hidden="true" />
                  )}
                  {todasOsAbertas ? 'Recolher OSs' : 'Expandir OSs'}
                </Button>
              )}
            </div>
          </div>

          {modoVisualizacao === 'dia' ? (
            <GradeDoDia
              linhas={linhasExibidas}
              dia={diaSelecionado}
              onEditarCelula={editarCelula}
            />
          ) : (
            <>
              <TotaisModalidadeCard linhas={linhasExibidas} />

              {agrupamento === 'OS' ? (
                <div className="space-y-2">
                  {[...linhasExibidas]
                    .sort((a, b) => a.numeroOs - b.numeroOs)
                    .map((linha) => (
                      <OsRow
                        key={linha.ordemServicoId}
                        linha={linha}
                        aberta={osAbertas.has(linha.ordemServicoId)}
                        mesAtivo={mes === competenciaAtual()}
                        onToggle={() => alternarOs(linha.ordemServicoId)}
                        onEditarKm={editarKm}
                        onEditarCelula={editarCelula}
                        onAplicarLote={aplicarEmLote}
                      />
                    ))}
                  {linhasExibidas.length === 0 && (
                    <EmptyState
                      title="Nenhuma OS com o status selecionado"
                      description="Ajuste o filtro de status para ver outras OS deste mês."
                    />
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {[...gruposDre.entries()].map(([id, linhasGrupo]) => {
                    const linhasDeExibicao = (exibidasPorDre.get(id) ?? []).sort(
                      (a, b) => a.numeroOs - b.numeroOs,
                    );
                    const primeira = linhasGrupo[0]!;
                    const aguardando = linhasGrupo.filter((l) => l.status === 'ENVIADO_SME').length;
                    const aprovados = linhasGrupo.filter(
                      (l) => l.status === 'APROVADO_SME' || l.status === 'RECEBIDO_DTP',
                    ).length;
                    const devolvidos = linhasGrupo.filter((l) => l.status === 'DEVOLVIDO').length;
                    const rascunhos = linhasGrupo.filter((l) => l.status === 'RASCUNHO_DRE').length;
                    const credenciadosUnicos = new Set(linhasGrupo.map((l) => l.credenciadoId))
                      .size;
                    const lancadas = linhasGrupo.filter((l) => diasPreenchidos(l) > 0).length;
                    const pendentes = linhasGrupo.filter(
                      (l) => diasPreenchidos(l) < l.diasLetivos.length,
                    ).length;
                    const totalDiasUteis = linhasGrupo.reduce(
                      (acc, l) => acc + l.diasLetivos.length,
                      0,
                    );
                    const totalDiasFeitos = linhasGrupo.reduce(
                      (acc, l) => acc + diasPreenchidos(l),
                      0,
                    );
                    const percentual =
                      totalDiasUteis > 0 ? Math.round((totalDiasFeitos / totalDiasUteis) * 100) : 0;
                    const statusCard = resolverStatusCardDre(linhasGrupo);
                    const loteEmAndamento = aguardando + aprovados + devolvidos > 0;
                    const aberta = dresAbertas.has(id);

                    return (
                      <Collapsible key={id} open={aberta} onOpenChange={() => alternarDre(id)}>
                        <Card>
                          <CollapsibleTrigger asChild>
                            <button
                              type="button"
                              className="flex w-full flex-col gap-2 p-4 text-left hover:bg-muted/40"
                            >
                              <div className="flex flex-wrap items-center gap-2">
                                {aberta ? (
                                  <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                                )}
                                <Building2
                                  className="h-4 w-4 shrink-0 text-muted-foreground"
                                  aria-hidden="true"
                                />
                                <span className="font-medium">
                                  DRE {primeira.dreCodigo ?? primeira.dreId} · {primeira.dreNome}
                                </span>
                                <Badge variant="outline">{natureza}</Badge>
                                <TomBadge
                                  tone={STATUS_CARD_DRE_INFO[statusCard].tone}
                                  label={STATUS_CARD_DRE_INFO[statusCard].label}
                                />
                                {devolvidos > 0 && (
                                  <TomBadge tone="warning" label={`${devolvidos} devolvida(s)`} />
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                <span>{linhasGrupo.length} OS Ativa(s)</span>
                                <span>Lançadas {lancadas}</span>
                                <span className={pendentes > 0 ? 'text-warning' : undefined}>
                                  Pendentes {pendentes}
                                </span>
                                <span>{credenciadosUnicos} credenciado(s)</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <Progress value={percentual} className="h-2 max-w-xs" />
                                <span className="text-xs text-muted-foreground">{percentual}%</span>
                              </div>
                            </button>
                          </CollapsibleTrigger>

                          <CardContent className="flex flex-wrap items-center gap-2 border-t p-3">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => exportarDreCsv(primeira.dreCodigo, linhasGrupo)}
                            >
                              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
                              Exportar CSV
                            </Button>

                            <div className="ml-auto flex flex-wrap items-center gap-2">
                              {loteEmAndamento ? (
                                <>
                                  <span className="text-sm text-muted-foreground">Lote SME</span>
                                  <Badge variant="secondary">
                                    {aguardando} aguardando · {aprovados} aprovados · {devolvidos}{' '}
                                    devolvidos
                                  </Badge>
                                  {aguardando > 0 && (
                                    <>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                          definirDevolvendo({
                                            dreId: id,
                                            dreNome: primeira.dreNome ?? id,
                                          })
                                        }
                                      >
                                        <ThumbsDown className="mr-2 h-4 w-4" aria-hidden="true" />
                                        Devolver lote
                                      </Button>
                                      <Button type="button" size="sm" onClick={avisarIndisponivel}>
                                        <ThumbsUp className="mr-2 h-4 w-4" aria-hidden="true" />
                                        Aprovar lote ({aguardando})
                                      </Button>
                                    </>
                                  )}
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      definirConferindo({
                                        dreId: id,
                                        dreNome: primeira.dreNome ?? id,
                                      })
                                    }
                                  >
                                    <Users className="mr-2 h-4 w-4" aria-hidden="true" />
                                    Conferir {credenciadosUnicos} cred.
                                  </Button>
                                </>
                              ) : rascunhos > 0 ? (
                                <>
                                  <span className="text-sm text-muted-foreground">
                                    {rascunhos} OS(s) pronta(s) para envio
                                  </span>
                                  <Button type="button" size="sm" onClick={avisarIndisponivel}>
                                    <Send className="mr-2 h-4 w-4" aria-hidden="true" />
                                    Enviar lote pronto
                                  </Button>
                                </>
                              ) : null}
                            </div>
                          </CardContent>

                          <CollapsibleContent>
                            <CardContent className="space-y-2 border-t p-3">
                              {linhasDeExibicao.length === 0 ? (
                                <p className="py-2 text-sm text-muted-foreground">
                                  Nenhuma OS com o status selecionado nesta DRE.
                                </p>
                              ) : (
                                linhasDeExibicao.map((linha) => (
                                  <OsRow
                                    key={linha.ordemServicoId}
                                    linha={linha}
                                    aberta={osAbertas.has(linha.ordemServicoId)}
                                    mesAtivo={mes === competenciaAtual()}
                                    onToggle={() => alternarOs(linha.ordemServicoId)}
                                    onEditarKm={editarKm}
                                    onEditarCelula={editarCelula}
                                    onAplicarLote={aplicarEmLote}
                                  />
                                ))
                              )}
                            </CardContent>
                          </CollapsibleContent>
                        </Card>
                      </Collapsible>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}

      <ResponsiveModal
        open={conferindo !== null}
        onOpenChange={(open) => !open && definirConferindo(null)}
        title={`Credenciados — DRE ${conferindo?.dreNome ?? ''}`}
      >
        <ul className="divide-y divide-border">
          {(conferindo ? (gruposDre.get(conferindo.dreId) ?? []) : []).map((linha) => (
            <li key={linha.ordemServicoId} className="flex items-center justify-between gap-3 py-2">
              <div>
                <p className="text-sm font-medium">{linha.credenciadoNome}</p>
                <p className="text-xs text-muted-foreground">OS {linha.numeroOs}</p>
              </div>
              <TomBadge
                tone={STATUS_OS_INFO[linha.status].tone}
                label={STATUS_OS_INFO[linha.status].label}
              />
            </li>
          ))}
        </ul>
      </ResponsiveModal>

      <ResponsiveModal
        open={devolvendo !== null}
        onOpenChange={(open) => {
          if (!open) {
            definirDevolvendo(null);
            definirMotivoDevolucao('');
          }
        }}
        title={`Devolver lote — DRE ${devolvendo?.dreNome ?? ''}`}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                definirDevolvendo(null);
                definirMotivoDevolucao('');
              }}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={motivoDevolucao.trim().length === 0}
              onClick={avisarIndisponivel}
            >
              Devolver
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          {/* Aviso fixo: o aviso passageiro some, e quem audita uma captura do
              diálogo não pode concluir que o lote foi devolvido. */}
          <Alert>
            <AlertDescription>
              Devolver lote ainda não está disponível. Você pode preencher o motivo, mas nada é
              devolvido e a DRE não é avisada.
            </AlertDescription>
          </Alert>
          <div className="space-y-2">
            <Label htmlFor="motivo-devolucao-lote">Motivo da devolução</Label>
            <Textarea
              id="motivo-devolucao-lote"
              value={motivoDevolucao}
              onChange={(e) => definirMotivoDevolucao(e.target.value)}
              placeholder="Descreva o que precisa ser corrigido pela DRE…"
            />
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
}

interface OsRowProps {
  linha: ApontamentoDiaLinha;
  aberta: boolean;
  /** Fora do mês ativo de digitação (mês corrente): grade inteira vira
   * somente leitura, qualquer que seja o status da OS — mesma regra do
   * protótipo (`competenciaAtiva.ts`, `statusMesDigitacao().bloqueado`). */
  mesAtivo: boolean;
  onToggle: () => void;
  onEditarKm: (ordemServicoId: string, valor: string) => void;
  onEditarCelula: (
    ordemServicoId: string,
    dia: number,
    sub: SubLinhaApontamento,
    valor: number | boolean,
  ) => void;
  onAplicarLote: (
    ordemServicoId: string,
    dias: number[],
    sub: SubLinhaApontamento,
    valor: number,
  ) => void;
}

/** OS enviada/aprovada/recebida: só consulta, edição volta se a SME devolver. */
function osBloqueada(status: StatusApontamentoOs): boolean {
  return status === 'ENVIADO_SME' || status === 'APROVADO_SME' || status === 'RECEBIDO_DTP';
}

function OsRow({
  linha,
  aberta,
  mesAtivo,
  onToggle,
  onEditarKm,
  onEditarCelula,
  onAplicarLote,
}: OsRowProps) {
  const totalDevidos = linha.diasLetivos.length;
  const preenchidos = diasPreenchidos(linha);
  const bloqueada = !mesAtivo || osBloqueada(linha.status);

  return (
    <Collapsible open={aberta} onOpenChange={onToggle}>
      <Card className={cn('border-l-4', OS_ROW_BORDA[linha.status])}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full flex-col gap-1 p-3 text-left hover:bg-muted/40"
          >
            <div className="flex flex-wrap items-center gap-2">
              {aberta ? (
                <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0" aria-hidden="true" />
              )}
              <span className="font-semibold tabular-nums">OS #{linha.numeroOs}</span>
              {linha.condutorNome && (
                <span className="inline-flex items-center gap-1 text-xs text-foreground/85">
                  <User className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                  {linha.condutorNome}
                </span>
              )}
              {linha.condutorCrmc && (
                <span className="inline-flex items-center gap-0.5 rounded border border-border/60 px-1 py-px font-mono text-[10px] text-muted-foreground">
                  <IdCard className="h-2.5 w-2.5" aria-hidden="true" />
                  {linha.condutorCrmc}
                </span>
              )}
              {linha.placaVeiculo && (
                <span className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground">
                  <Car className="h-3 w-3" aria-hidden="true" />
                  {linha.placaVeiculo}
                </span>
              )}
              {!bloqueada && totalDevidos > 0 && totalDevidos - preenchidos > 0 && (
                <Badge variant="outline" className="gap-1 text-warning">
                  {totalDevidos - preenchidos}{' '}
                  {totalDevidos - preenchidos === 1 ? 'dia sem lançto' : 'dias sem lançto'}
                </Badge>
              )}
              <div className="ml-auto flex shrink-0 items-center gap-2">
                {bloqueada && (
                  <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                )}
                <TomBadge
                  tone={STATUS_OS_INFO[linha.status].tone}
                  label={STATUS_OS_INFO[linha.status].label}
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 pl-6 text-xs text-muted-foreground">
              <Building2 className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{linha.credenciadoNome}</span>
              <span aria-hidden="true">·</span>
              <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                {linha.natureza}
              </Badge>
              {linha.dreCodigo && (
                <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                  DRE {linha.dreCodigo}
                </Badge>
              )}
              <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                {MODALIDADE_LABEL[linha.modalidade]}
              </Badge>
              {linha.tipoVeiculo && (
                <span className="inline-flex items-center gap-1">
                  <Bus className="h-3 w-3" aria-hidden="true" />
                  {linha.tipoVeiculo}
                </span>
              )}
              {linha.monitorNome && (
                <span className="inline-flex items-center gap-1">
                  <span aria-hidden="true">·</span>
                  <Users className="h-3 w-3" aria-hidden="true" />
                  {linha.monitorNome}
                </span>
              )}
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-3 border-t p-3">
            {bloqueada && (
              <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-primary">
                <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>
                  {!mesAtivo
                    ? 'Esta competência não é o mês ativo de digitação. A grade fica somente leitura — volte para o mês ativo pra editar.'
                    : 'Esta OS já foi enviada para a SME. Os lançamentos ficam disponíveis apenas para consulta. Edição volta a ser liberada se a SME devolver o lote.'}
                </span>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Label htmlFor={`${linha.ordemServicoId}-km`} className="whitespace-nowrap text-xs">
                KM rodado no mês
              </Label>
              <Input
                id={`${linha.ordemServicoId}-km`}
                inputMode="decimal"
                className="h-8 w-32"
                value={linha.km}
                disabled={bloqueada}
                onChange={(e) => onEditarKm(linha.ordemServicoId, e.target.value)}
              />
            </div>

            <GradeMensalOs
              linha={linha}
              bloqueada={bloqueada}
              onEditarCelula={onEditarCelula}
              onAplicarLote={onAplicarLote}
            />

            <p className="text-xs text-muted-foreground">
              {totalDevidos} {totalDevidos === 1 ? 'dia letivo' : 'dias letivos'} no mês
            </p>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

/** Visão "Por dia": uma linha por OS, uma coluna por sublinha, só o dia
 * selecionado — foco em digitação rápida de um único dia (mobile/tablet),
 * mesma ideia do modo "Por dia" do protótipo. Reusa `CelulaGrade`, a mesma
 * célula da grade mensal. */
function GradeDoDia({
  linhas,
  dia,
  onEditarCelula,
}: {
  linhas: ApontamentoDiaLinha[];
  dia: number;
  onEditarCelula: (
    ordemServicoId: string,
    dia: number,
    sub: SubLinhaApontamento,
    valor: number | boolean,
  ) => void;
}) {
  if (linhas.length === 0) {
    return (
      <EmptyState
        title="Nenhuma OS ativa no mês"
        description="Ajuste a competência, a DRE, a natureza ou a busca."
      />
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border bg-background">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-muted/60">
          <tr>
            <th className="sticky left-0 z-10 min-w-[220px] border-b border-r bg-muted/60 p-2 text-left text-xs font-medium">
              OS
            </th>
            {SUBLINHAS.map((sub) => (
              <th
                key={sub}
                className="w-20 border-b border-r p-2 text-center text-[11px] font-medium"
              >
                {SUBLINHA_LABEL[sub]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...linhas]
            .sort((a, b) => a.numeroOs - b.numeroOs)
            .map((linha) => {
              const letivo = linha.diasLetivos.includes(dia);
              const fimVigencia = vigenciaFimDia(linha);
              const foraVigencia = fimVigencia != null && dia > fimVigencia;
              const bloqueada = osBloqueada(linha.status);
              return (
                <tr key={linha.ordemServicoId} className="border-b hover:bg-muted/20">
                  <td className="sticky left-0 z-10 border-r bg-background p-2">
                    <p className="text-sm font-medium">OS #{linha.numeroOs}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {linha.credenciadoNome} · DRE {linha.dreCodigo}
                    </p>
                  </td>
                  {SUBLINHAS.map((sub) => (
                    <CelulaGrade
                      key={sub}
                      ordemServicoId={linha.ordemServicoId}
                      dia={dia}
                      sub={sub}
                      letivo={letivo}
                      foraVigencia={foraVigencia}
                      celula={linha.celulas[dia]}
                      bloqueada={bloqueada}
                      onEditarCelula={onEditarCelula}
                    />
                  ))}
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}

interface GradeMensalOsProps {
  linha: ApontamentoDiaLinha;
  bloqueada: boolean;
  onEditarCelula: (
    ordemServicoId: string,
    dia: number,
    sub: SubLinhaApontamento,
    valor: number | boolean,
  ) => void;
  onAplicarLote: (
    ordemServicoId: string,
    dias: number[],
    sub: SubLinhaApontamento,
    valor: number,
  ) => void;
}

/** Grade sublinha × dia de uma OS — cópia estrutural da grade do protótipo
 * (dots por status, legenda, coluna Total e Ações), com edição simplificada:
 * clique abre um popover com a quantidade, sem split por modalidade nem
 * registro de motivo — isso depende do motor de cálculo, que ainda não
 * existe (CLAUDE.md, "Regras de interface", item 7). */
function GradeMensalOs({ linha, bloqueada, onEditarCelula, onAplicarLote }: GradeMensalOsProps) {
  const partesMes = linha.mesReferencia.slice(0, 7).split('-');
  const ano = Number(partesMes[0]);
  const mesNum = Number(partesMes[1]);
  const totalDiasMes = new Date(ano, mesNum, 0).getDate();
  const dias = Array.from({ length: totalDiasMes }, (_, i) => i + 1);
  const diasLetivos = new Set(linha.diasLetivos);
  const fimVigencia = vigenciaFimDia(linha);

  return (
    <div className="overflow-x-auto rounded-lg border bg-background">
      <div className="flex flex-col gap-1.5 border-b bg-muted/30 px-2 py-1.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-[11px] text-muted-foreground">
            {bloqueada
              ? 'Passe o mouse sobre uma célula para ver a quantidade lançada. Edição indisponível para o seu perfil.'
              : 'Clique numa célula pra lançar a quantidade do dia.'}
          </span>
          <div className="ml-auto flex items-center gap-3">
            {bloqueada && (
              <Badge variant="secondary" className="h-5 gap-1 text-[10px] font-medium">
                <Eye className="h-3 w-3" aria-hidden="true" />
                Somente leitura
              </Badge>
            )}
            <div
              className="flex items-center gap-3 text-[11px] text-muted-foreground"
              aria-label="Legenda de cores das células"
            >
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full border border-muted-foreground/40 bg-background"
                  aria-hidden="true"
                />
                Disponível
              </span>
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full bg-muted-foreground/30"
                  aria-hidden="true"
                />
                Não letivo
              </span>
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full bg-warning"
                  aria-hidden="true"
                />
                Pendente
              </span>
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full bg-destructive/70"
                  aria-hidden="true"
                />
                Fora da vigência
              </span>
            </div>
          </div>
        </div>
      </div>

      <table className="w-full border-collapse text-sm">
        <thead className="bg-muted/60">
          <tr>
            <th className="sticky left-0 z-10 w-[110px] border-b border-r bg-muted/60 p-2 text-left text-xs font-medium">
              Sublinha
            </th>
            {dias.map((d) => {
              const letivo = diasLetivos.has(d);
              const fora = fimVigencia != null && d > fimVigencia;
              return (
                <th
                  key={d}
                  className={cn(
                    'w-11 border-b border-r p-1 text-center text-[11px] font-medium',
                    !letivo && 'bg-muted-foreground/10 text-muted-foreground',
                    fora && 'bg-destructive/10 text-destructive/70',
                  )}
                >
                  {String(d).padStart(2, '0')}
                </th>
              );
            })}
            <th className="w-[72px] border-b border-r bg-muted/30 p-2 text-right text-xs font-medium">
              Total
            </th>
            <th className="w-[96px] border-b bg-muted/40 p-2 text-center text-xs font-medium">
              Ações
            </th>
          </tr>
        </thead>
        <tbody>
          {SUBLINHAS.map((sub) => {
            const diasVaziosDaSublinha = linha.diasLetivos.filter(
              (d) =>
                (fimVigencia == null || d <= fimVigencia) &&
                valorSublinha(linha.celulas[d], sub) === 0,
            );
            let total = 0;
            for (const d of linha.diasLetivos) total += valorSublinha(linha.celulas[d], sub);

            return (
              <tr key={sub} className="border-b hover:bg-muted/20">
                <td className="sticky left-0 z-10 border-r bg-background p-2 text-xs font-medium text-muted-foreground">
                  {SUBLINHA_LABEL[sub]}
                </td>
                {dias.map((d) => (
                  <CelulaGrade
                    key={d}
                    ordemServicoId={linha.ordemServicoId}
                    dia={d}
                    sub={sub}
                    letivo={diasLetivos.has(d)}
                    foraVigencia={fimVigencia != null && d > fimVigencia}
                    celula={linha.celulas[d]}
                    bloqueada={bloqueada}
                    onEditarCelula={onEditarCelula}
                  />
                ))}
                <td className="border-r bg-muted/30 px-2 py-1.5 text-right text-xs font-semibold tabular-nums">
                  {sub === 'FALTA_VEICULO' ? '—' : total}
                </td>
                <td className="px-2 py-1.5 text-center">
                  {sub !== 'FALTA_VEICULO' && !bloqueada && (
                    <AplicarLoteButton
                      ordemServicoId={linha.ordemServicoId}
                      sub={sub}
                      diasVazios={diasVaziosDaSublinha}
                      onAplicarLote={onAplicarLote}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface CelulaGradeProps {
  ordemServicoId: string;
  dia: number;
  sub: SubLinhaApontamento;
  letivo: boolean;
  foraVigencia: boolean;
  celula: CelulaApontamentoDia | undefined;
  bloqueada: boolean;
  onEditarCelula: (
    ordemServicoId: string,
    dia: number,
    sub: SubLinhaApontamento,
    valor: number | boolean,
  ) => void;
}

function CelulaGrade({
  ordemServicoId,
  dia,
  sub,
  letivo,
  foraVigencia,
  celula,
  bloqueada,
  onEditarCelula,
}: CelulaGradeProps) {
  const [aberto, definirAberto] = useState(false);
  const isFaltaVeiculo = sub === 'FALTA_VEICULO';
  const valor = isFaltaVeiculo ? 0 : valorSublinha(celula, sub);
  const faltaVeiculoAtiva = isFaltaVeiculo && !!celula?.faltaVeiculo;
  const vazio = letivo && !foraVigencia && !isFaltaVeiculo && valor === 0;

  if (!letivo || foraVigencia) {
    return (
      <td
        className={cn(
          'border-r p-0 text-center',
          !letivo && 'bg-muted-foreground/10',
          foraVigencia && 'bg-destructive/10',
        )}
        title={foraVigencia ? 'Fora da vigência da OS' : undefined}
      >
        <span className="flex h-9 w-full items-center justify-center text-xs text-muted-foreground/80">
          {foraVigencia ? '×' : '·'}
        </span>
      </td>
    );
  }

  if (isFaltaVeiculo) {
    return (
      <td className="border-r p-0 text-center">
        <button
          type="button"
          disabled={bloqueada}
          onClick={() => onEditarCelula(ordemServicoId, dia, sub, !faltaVeiculoAtiva)}
          title={
            bloqueada
              ? undefined
              : faltaVeiculoAtiva
                ? 'Veículo não operou — clique para desmarcar'
                : 'Registrar falta do veículo no dia'
          }
          className={cn(
            'flex h-9 w-full items-center justify-center text-xs outline-hidden transition-colors',
            !bloqueada && 'hover:bg-destructive/10',
            bloqueada && 'cursor-help',
            faltaVeiculoAtiva
              ? 'bg-destructive/15 font-semibold text-destructive'
              : 'text-muted-foreground',
          )}
        >
          {faltaVeiculoAtiva ? <Ban className="h-4 w-4" aria-hidden="true" /> : '—'}
        </button>
      </td>
    );
  }

  return (
    <td className={cn('relative border-r p-0 text-center', vazio && !bloqueada && 'bg-warning/10')}>
      <Popover open={aberto} onOpenChange={definirAberto}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={bloqueada}
            title={
              bloqueada
                ? undefined
                : valor > 0
                  ? `${valor} — clique para editar`
                  : 'Registrar quantidade'
            }
            className={cn(
              'flex h-9 w-full items-center justify-center text-xs tabular-nums outline-hidden transition-colors',
              !bloqueada && 'hover:bg-muted/60 focus:bg-primary/10',
              bloqueada && 'cursor-help text-foreground/80',
              valor > 0 ? 'font-medium text-foreground' : 'text-muted-foreground',
            )}
          >
            {valor > 0 ? valor : '—'}
          </button>
        </PopoverTrigger>
        {!bloqueada && (
          <PopoverContent className="w-40 p-2" align="center">
            <Label htmlFor={`qtd-${ordemServicoId}-${sub}-${dia}`} className="text-xs">
              Quantidade
            </Label>
            <Input
              id={`qtd-${ordemServicoId}-${sub}-${dia}`}
              type="number"
              min={0}
              defaultValue={valor}
              className="mt-1 h-8"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onEditarCelula(ordemServicoId, dia, sub, Number(e.currentTarget.value));
                  definirAberto(false);
                }
              }}
              onBlur={(e) => {
                onEditarCelula(ordemServicoId, dia, sub, Number(e.target.value));
                definirAberto(false);
              }}
            />
          </PopoverContent>
        )}
      </Popover>
    </td>
  );
}

function AplicarLoteButton({
  ordemServicoId,
  sub,
  diasVazios,
  onAplicarLote,
}: {
  ordemServicoId: string;
  sub: SubLinhaApontamento;
  diasVazios: number[];
  onAplicarLote: (
    ordemServicoId: string,
    dias: number[],
    sub: SubLinhaApontamento,
    valor: number,
  ) => void;
}) {
  const [aberto, definirAberto] = useState(false);

  return (
    <Popover open={aberto} onOpenChange={definirAberto}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          disabled={diasVazios.length === 0}
          title="Aplicar em lote"
          aria-label={`Aplicar em lote — ${SUBLINHA_LABEL[sub]}`}
        >
          <Copy className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 space-y-2 p-3" align="end">
        <p className="text-xs text-muted-foreground">
          Aplica o mesmo valor nos {diasVazios.length} dias letivos ainda sem lançamento nesta
          sublinha.
        </p>
        <Label htmlFor={`lote-${ordemServicoId}-${sub}`} className="text-xs">
          Quantidade
        </Label>
        <Input
          id={`lote-${ordemServicoId}-${sub}`}
          type="number"
          min={0}
          className="h-8"
          defaultValue={0}
        />
        <Button
          type="button"
          size="sm"
          className="w-full"
          onClick={(e) => {
            const input = (e.currentTarget.parentElement?.querySelector('input') ??
              null) as HTMLInputElement | null;
            const valor = Number(input?.value ?? 0);
            onAplicarLote(ordemServicoId, diasVazios, sub, valor);
            definirAberto(false);
          }}
        >
          Aplicar em {diasVazios.length} dia(s)
        </Button>
      </PopoverContent>
    </Popover>
  );
}

const OS_ROW_BORDA: Record<StatusApontamentoOs, string> = {
  RASCUNHO_DRE: 'border-l-muted-foreground/40',
  ENVIADO_SME: 'border-l-info',
  APROVADO_SME: 'border-l-success',
  RECEBIDO_DTP: 'border-l-success',
  DEVOLVIDO: 'border-l-destructive',
};

/** Card colapsável com totais por modalidade — cópia estrutural do
 * `TotaisModalidadeCard` do protótipo. A dimensão "tipo de atendimento"
 * (EMEI/EMEF/CEI…) não veio: nosso modelo de OS não carrega essa informação
 * ainda (fica pra quando a grade de apontamento ganhar vínculo com escola). */
function TotaisModalidadeCard({ linhas }: { linhas: ApontamentoDiaLinha[] }) {
  const [aberto, definirAberto] = useState(false);

  const porModalidade = useMemo(() => {
    const mapa = new Map<ModalidadeAtendimento, { os: number; creds: Set<string> }>();
    for (const l of linhas) {
      const atual = mapa.get(l.modalidade) ?? { os: 0, creds: new Set<string>() };
      atual.os += 1;
      atual.creds.add(l.credenciadoId);
      mapa.set(l.modalidade, atual);
    }
    return [...mapa.entries()]
      .map(([modalidade, v]) => ({ modalidade, os: v.os, creds: v.creds.size }))
      .sort((a, b) => b.os - a.os);
  }, [linhas]);

  const totalOs = linhas.length;
  const totalCreds = new Set(linhas.map((l) => l.credenciadoId)).size;

  if (linhas.length === 0) return null;

  return (
    <Card>
      <Collapsible open={aberto} onOpenChange={definirAberto}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-3 rounded-t-xl p-3 text-left hover:bg-muted/40"
            aria-label="Alternar totais por modalidade"
          >
            <div className="flex min-w-0 items-center gap-2">
              <Layers className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span className="text-sm font-medium">
                Totais por modalidade e tipo de atendimento
              </span>
              <Badge variant="secondary" className="text-[10px]">
                {totalOs} OS · {totalCreds} credenciado(s)
              </Badge>
            </div>
            <div className="flex items-center gap-1">
              <div className="mr-2 hidden items-center gap-1 md:flex">
                {porModalidade.slice(0, 3).map((m) => (
                  <Badge key={m.modalidade} variant="outline" className="text-[10px]">
                    {MODALIDADE_LABEL[m.modalidade]}: {m.os}
                  </Badge>
                ))}
              </div>
              {aberto ? (
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              )}
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-3 p-4 pt-0">
            <div className="h-px bg-border" />
            <section aria-labelledby="totais-mod">
              <header className="mb-2 flex items-center justify-between">
                <h4
                  id="totais-mod"
                  className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  Por modalidade
                </h4>
                <span className="text-[10px] text-muted-foreground">
                  {porModalidade.length} modalidade(s)
                </span>
              </header>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {porModalidade.map((m) => {
                  const pct = totalOs ? Math.round((m.os / totalOs) * 100) : 0;
                  return (
                    <div
                      key={m.modalidade}
                      className="rounded-lg border bg-card px-3 py-2.5 transition hover:bg-muted/40"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold">
                          {MODALIDADE_LABEL[m.modalidade]}
                        </span>
                        <Badge variant="secondary" className="text-[10px] tabular-nums">
                          {pct}%
                        </Badge>
                      </div>
                      <div className="mt-1 flex items-baseline gap-1.5">
                        <span className="text-xl font-bold leading-none tabular-nums">{m.os}</span>
                        <span className="text-[11px] text-muted-foreground">OS</span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">
                        {m.creds} credenciado(s)
                      </div>
                      <div className="mt-2 h-1 overflow-hidden rounded-full bg-muted">
                        <div className="h-full bg-accent/80" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export default ApontamentoGridPage;
